import { useCurrentUser } from "../../api/current-user";
import { useSite } from "../../api/site-query";
import { createRestrictiveInjectionState } from "../../composable/restrictive-injection-state";

export type DashboardContext = {
	site: string;
	publicPage?: string;
};

export const DashboardViewMode = {
	Maximize: "maximize",
	Edit: "edit",
	Default: "default",
} as const;
export type DashboardViewMode = (typeof DashboardViewMode)[keyof typeof DashboardViewMode];

export type DashboardView =
	| {
			type: typeof DashboardViewMode.Maximize | typeof DashboardViewMode.Edit;
			id: string;
	  }
	| { type: typeof DashboardViewMode.Default };

export const [provideDashboardState, useDashboardState] = createRestrictiveInjectionState(() => {
	const context = ref<DashboardContext>();

	const liveMode = useLocalStorage("dashboard-live-mode", false);
	const lockCursor = useLocalStorage("dashboard-cursor-lock", false);

	// Sync liveMode deactivate with the lockCursor
	watch(liveMode, () => {
		if (!liveMode.value) {
			lockCursor.value = false;
		}
	});

	const view = ref<DashboardView>({ type: "default" });

	const siteId = computed(() => context.value?.site || "");
	const { site } = useSite(siteId);
	const timezone = computed(() => site.value?.timezone);

	const { currentUser } = useCurrentUser();
	const locale = computed(() => currentUser.value?.preferredLang);

	function setEditView(id: string) {
		view.value = {
			type: "edit",
			id,
		};
	}

	function setMaximizeView(id: string) {
		view.value = {
			type: "maximize",
			id,
		};
	}

	function setDefaultView() {
		view.value = { type: "default" };
	}

	return {
		context,
		liveMode,
		lockCursor,
		view,
		timezone,
		locale,
		setEditView,
		setMaximizeView,
		setDefaultView,
	};
});
